import { Link, Modal, TextInput } from '@pmi/dsm-react';
import { Formik, FormikHelpers } from 'formik';
import { ReactNode } from 'react';
import { Form } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';
import { toastWithAnalytics } from '../../../../../../shared/analytics';
import {
  getTextForNoPersonId,
  getUserText,
  normalizeEmail,
  singleEmailRegex,
} from '../../../../../../shared/helpers';
import { User } from '../../../../../../shared/models/UserModels';
import { accountSelector } from '../../../../../../shared/store/shared/accountSlice';
import { useUpdateUserEmailMutation } from './apiSlice';
import styles from './styles.module.scss';

export interface ChangeEmailAddressModalProps {
  user: User;
  onCancel?: () => void;
}

interface FormModel {
  email: string;
}

export default function ChangeEmailAddressModal({
  user,
  onCancel = () => {},
}: ChangeEmailAddressModalProps) {
  const { id, personName } = user;
  const { currentAccountId } = useSelector(accountSelector);
  const onCancelHandler = () => {
    onCancel();
  };

  const initialData: FormModel = {
    email: '',
  };

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .trim()
      .required('Email is required')
      .test(
        'single-email',
        'Please enter a valid email address',
        (value) => !value || singleEmailRegex.test(normalizeEmail(value)),
      ),
  });

  const [updateUserEmail, updatedUserEmailStatus] =
    useUpdateUserEmailMutation();

  const onSubmit = async (
    values: FormModel,
    action: FormikHelpers<FormModel>,
  ) => {
    action.setSubmitting(true);
    const newEmail = normalizeEmail(values.email);

    try {
      await updateUserEmail({
        accountId: currentAccountId,
        userId: id || '',
        email: newEmail,
      }).unwrap();

      const message = (): ReactNode => (
        <>
          You have updated <span data-exclude-lg>{getUserText(user)}</span>
        </>
      );
      toastWithAnalytics(message(), 'success');
      updatedUserEmailStatus.reset();
      action.resetForm();
      onCancel();
    } catch (e: any) {
      let errorMsg;
      try {
        const parsed = JSON.parse(e?.data?.Value || e?.data?.ErrorCode);
        errorMsg = parsed?.error || parsed?.detail || parsed?.message;
      } catch {}

      const finalError =
        errorMsg ||
        e?.data?.detail ||
        'An unexpected error occurred. Please try again.';

      action.setFieldError('email', finalError);
      toastWithAnalytics(finalError, 'danger');
    } finally {
      action.setSubmitting(false);
    }
  };

  if (!user) return null;

  return (
    <Formik
      initialValues={initialData}
      validateOnChange
      validateOnBlur
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {({
        submitForm,
        dirty,
        errors,
        handleChange,
        values,
        isValid,
        isSubmitting,
      }) => (
        <Modal
          show
          customClassName={styles.changeEmailContainer}
          onClose={onCancelHandler}
          headline="Change Email Address"
          variant="input"
          buttons={[
            {
              titleText: 'Cancel',
              variant: 'outline-secondary',
              onClick: onCancelHandler,
              disabled: isSubmitting,
            },
            {
              titleText: 'Update Email',
              enforceTitleCase: false,
              variant: 'primary',
              onClick: submitForm,
              disabled: isSubmitting || !dirty || !isValid,
            },
          ]}
        >
          <div className="row align-items-start mt-3">
            <div className="col-6 mb-3">
              <h5 className="mb-2">{personName}</h5>
              <div
                id="viewUserModal-personId"
                className="d-flex align-items-center mb-2 flex-wrap"
              >
                <span className={`${styles.textSecondary}`}>PMI ID: </span>
                <strong
                  className={`ms-1 right-divider ${styles.textSecondary}`}
                >
                  {user.personId
                    ? user.personId
                    : getTextForNoPersonId(user, true)}
                </strong>
              </div>
              <div
                id="viewUserModal-email"
                className="d-flex align-items-center mb-2 gap-1 flex-wrap"
              >
                <span className={`${styles.textSecondary}`}>Email: </span>
                <Link
                  label={user.email}
                  href={`mailto:${user.email}`}
                  className={`${styles.monochrome} ${styles.textSecondary}`}
                />
              </div>
            </div>
            <div className="col-6 mb-3">
              <Form.Group
                controlId="email"
                className={errors?.email ? 'is-invalid' : undefined}
              >
                <Form.Label className="mb-2">New Email Address</Form.Label>
                <TextInput
                  id="email"
                  name="email"
                  maxLength={100}
                  onChange={handleChange}
                  value={values?.email}
                  isInvalid={!!errors?.email}
                  className={errors?.email ? 'is-invalid' : undefined}
                  validationMessage={errors?.email || ''}
                  disabled={isSubmitting}
                  data-testid="new-email-input"
                />
              </Form.Group>
            </div>
          </div>
        </Modal>
      )}
    </Formik>
  );
}
